/**
 * Deriver hook (AC 10.1/10.3). Reads COMMITTED content only and precipitates truth/decision
 * cards linked back to their source spans. Fires on ACCEPTANCE (the `/commit` action), never on
 * provisional edits — so nothing derives from text the author has not stood behind (OQ-4).
 */

import type { DocRange, ParticipantRef } from '../types';

/** A committed span handed to the deriver. */
export interface CommittedSpan {
  range: DocRange;
  text: string;
  committedBy: string;
}

/** A card the deriver precipitates from committed content. */
export interface DerivedCard {
  kind: 'truth' | 'decision';
  statement: string;
  /** The source span this card was derived from (provenance / back-link). */
  source: DocRange;
}

export interface Deriver {
  /** Default participant identity attributed to derived cards (OQ-9: a thinker id). */
  readonly participant: ParticipantRef;
  /** Precipitate cards from a newly committed span. May be async (calls a thinker). */
  derive(span: CommittedSpan): Promise<DerivedCard[]>;
}

/** A no-op deriver — the safe default until a thinker is assigned (OQ-4). */
export class NoopDeriver implements Deriver {
  readonly participant: ParticipantRef = {
    kind: 'ai',
    id: 'thinker:deriver-default',
    displayName: 'Deriver',
  };
  async derive(): Promise<DerivedCard[]> {
    return [];
  }
}
